
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    IconButton,
    Avatar,
    Chip,
    alpha,
    useTheme,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    TablePagination,
    DialogTitle,
    Grid,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Menu,
    ListItemIcon,
    ListItemText,
    Skeleton
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationOnIcon,
    Warning as WarningIcon,
    Add as AddIcon,
    Close as CloseIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { userService } from '../service/userService';
import { getImageUrl } from '../service/api';
import SuccessNotification from '../components/SuccessNotification';

export default function Users() {
    const theme = useTheme();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Delete state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    // Add User state
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [userFormData, setUserFormData] = useState({
        fullName: '',
        mobile: '',
        role: 2
    });
    const [formErrors, setFormErrors] = useState({
        fullName: '',
        mobile: ''
    });

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers();
        }, 500);
        return () => clearTimeout(timer);
    }, [page, rowsPerPage, searchTerm]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second for smooth skeleton transition
            const [response] = await Promise.all([
                userService.getUsers(page + 1, rowsPerPage, searchTerm),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);

            if (response.success) {
                if (response.data && response.data.data) {
                    setUsers(response.data.data);
                    setTotalCount(response.data.totalRecords || 0);
                } else {
                    const data = response.data || [];
                    setUsers(data);
                    setTotalCount(data.length);
                }
            }
        } catch (error) {
            console.error("Error fetching users", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;
        setDeleteLoading(true);
        try {
            const response = await userService.deleteUser(userToDelete.userId);
            if (response.success) {
                fetchUsers();
                setDeleteModalOpen(false);
                setNotification({ open: true, message: 'User account deleted successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error("Error deleting user", error);
        } finally {
            setDeleteLoading(false);
            setUserToDelete(null);
        }
    };

    const handleViewOpen = (user) => {
        setSelectedUser(user);
        setViewModalOpen(true);
    };

    const handleMenuOpen = (event, user) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
    };

    const handleAddOpen = () => {
        setAddModalOpen(true);
        setUserFormData({ fullName: '', mobile: '', role: 2 });
        setFormErrors({ fullName: '', mobile: '' });
    };

    const handleAddSubmit = async () => {
        const errors = {
            fullName: !userFormData.fullName.trim() ? 'Full Name is required' : '',
            mobile: !userFormData.mobile.trim() ? 'Mobile Number is required' :
                userFormData.mobile.length !== 10 ? 'Enter a valid 10-digit number' : ''
        };

        setFormErrors(errors);

        if (errors.fullName || errors.mobile) {
            return;
        }

        setFormLoading(true);
        try {
            const response = await userService.createProfile(userFormData);
            if (response.success) {
                fetchUsers();
                setAddModalOpen(false);
                setNotification({ open: true, message: 'User added successfully!', severity: 'success' });
            } else {
                // Show error notification and keep modal open
                setNotification({
                    open: true,
                    message: response.message || 'Failed to add user',
                    severity: 'error'
                });
                // Optionally set field-level error for mobile number
                if (response.message && response.message.toLowerCase().includes('mobile')) {
                    setFormErrors({ ...errors, mobile: response.message });
                }
            }
        } catch (error) {
            console.error('Error adding user:', error);
            setNotification({
                open: true,
                message: error.message || 'Something went wrong!',
                severity: 'error'
            });
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <Box sx={{ pb: 5 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                gap: 2,
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>User Management</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddOpen}
                    sx={{
                        borderRadius: 2.5,
                        px: 3,
                        py: 1.2,
                        width: { xs: '100%', sm: 'auto' },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        background: 'primary.main',
                        boxShadow: `0 4px 14px 0 ${alpha(theme.palette.secondary.main, 0.39)}`,
                        '&:hover': {
                            background: 'primary.dark',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.23)}`
                        }
                    }}
                >
                    Add User
                </Button>
            </Box>

            <Paper sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                border: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        placeholder="Search users by name or mobile..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: '#f8fafc',
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover': { bgcolor: '#f1f5f9' },
                                transition: 'all 0.2s'
                            }
                        }}
                    />
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="medium" sx={{ minWidth: 800 }}>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>User Profile</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Contact Info</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Constituency</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from(new Array(rowsPerPage)).map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ width: '100%' }}>
                                                    <Skeleton width="60%" />
                                                    <Skeleton width="40%" />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell><Skeleton width="80%" /></TableCell>
                                        <TableCell><Skeleton width="60%" /></TableCell>
                                        <TableCell><Skeleton width="30%" height={32} sx={{ borderRadius: 2 }} /></TableCell>
                                        <TableCell><Skeleton width="30%" height={32} sx={{ borderRadius: 2 }} /></TableCell>
                                    </TableRow>
                                ))
                            ) : (users.length > rowsPerPage ? users.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : users).map((user) => (
                                <TableRow key={user.userId} hover sx={{ '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.5) !important' }, transition: 'all 0.2s' }}>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, user)}
                                            sx={{ color: '#64748b', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                src={getImageUrl(user.profilePicPath)}
                                                sx={{
                                                    width: 42,
                                                    height: 42,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    border: '2px solid white'
                                                }}
                                            >
                                                {user.fullName ? user.fullName.charAt(0) : <PersonIcon />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>{user.fullName}</Typography>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>Joined {new Date(user.createdAt || Date.now()).toLocaleDateString()}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            <Typography variant="body2">{user.mobile || 'N/A'}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{user.constituency || 'Not Assigned'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.role === 'Admin' ? 'Admin' : 'Member'}
                                            size="small"
                                            color={user.role === 'Admin' ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.isActive ? 'Active' : 'Inactive'}
                                            size="small"
                                            variant="outlined"
                                            color={user.isActive ? 'primary' : 'secondary'}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 2,
                                                minWidth: 70,
                                                borderWidth: 2,
                                                '& .MuiChip-label': { px: 1.5 }
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Add New User</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Full Name"
                            fullWidth
                            value={userFormData.fullName}
                            onChange={(e) => {
                                setUserFormData({ ...userFormData, fullName: e.target.value });
                                if (formErrors.fullName) setFormErrors({ ...formErrors, fullName: '' });
                            }}
                            error={!!formErrors.fullName}
                            helperText={formErrors.fullName}
                        />
                        <TextField
                            label="Mobile Number"
                            fullWidth
                            value={userFormData.mobile}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                setUserFormData({ ...userFormData, mobile: val });
                                if (formErrors.mobile) setFormErrors({ ...formErrors, mobile: '' });
                            }}
                            inputProps={{ maxLength: 10 }}
                            error={!!formErrors.mobile}
                            helperText={formErrors.mobile}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                label="Role"
                                value={userFormData.role}
                                onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                            >
                                <MenuItem value={2}>Member</MenuItem>
                                <MenuItem value={1}>Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddSubmit} disabled={formLoading}>
                        {formLoading ? <CircularProgress size={24} color="inherit" /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
                <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
                    <WarningIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700}>Delete User?</Typography>
                    <Typography variant="body2" color="text.secondary">Are you sure you want to delete {userToDelete?.fullName}?</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                    <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteLoading}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { minWidth: 150, borderRadius: 2, boxShadow: '0 44px 20px rgba(0,0,0,0.1)' }
                }}
            >
                <MenuItem onClick={() => { handleMenuClose(); handleViewOpen(selectedUser); }}>
                    <ListItemIcon><ViewIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="View" />
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); handleDeleteClick(selectedUser); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primary="Delete" />
                </MenuItem>
            </Menu>

            {/* View User Modal */}
            <Dialog
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    User Details
                    <IconButton onClick={() => setViewModalOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedUser && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
                            <Avatar
                                src={getImageUrl(selectedUser.profilePicPath)}
                                sx={{ width: 100, height: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            >
                                {selectedUser.fullName ? selectedUser.fullName.charAt(0) : <PersonIcon />}
                            </Avatar>

                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Full Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedUser.fullName}</Typography>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Role</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                label={selectedUser.role === 'Admin' ? 'Admin' : 'Member'}
                                                color={selectedUser.role === 'Admin' ? 'primary' : 'default'}
                                                size="small"
                                            />
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Status</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                label={selectedUser.isActive ? 'Active' : 'Inactive'}
                                                color={selectedUser.isActive ? 'primary' : 'secondary'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontWeight: 800, borderWidth: 2 }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Mobile</Typography>
                                        <Typography variant="body1">{selectedUser.mobile || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Membership ID</Typography>
                                        <Typography variant="body1">{selectedUser.membershipId || 'N/A'}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Gender</Typography>
                                        <Typography variant="body1">{selectedUser.gender || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Blood Group</Typography>
                                        <Typography variant="body1">{selectedUser.bloodGroup || 'N/A'}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Occupation</Typography>
                                        <Typography variant="body1">{selectedUser.occupation || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Constituency</Typography>
                                        <Typography variant="body1">{selectedUser.constituency || 'N/A'}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Address</Typography>
                                    <Typography variant="body2">{selectedUser.address || 'N/A'}</Typography>
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Referral Code</Typography>
                                        <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 700 }}>{selectedUser.autoGeneratedReferralCode || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Joined On</Typography>
                                        <Typography variant="body1">{new Date(selectedUser.createdAt || Date.now()).toLocaleDateString()}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setViewModalOpen(false)} variant="contained" fullWidth sx={{ borderRadius: 2 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification({ ...notification, open: false })}
            />
        </Box>
    );
}

